import type { PrimeVueConfiguration, PrimeVueLocaleOptions } from 'primevue/config'
import { definePreset } from '@primeuix/styled'
import Aura from '@primevue/themes/aura'

import { buttonsConfig } from './button/index'
import { cardConfig, cardPt } from './card'
import { checkBoxConfig } from './checkbox'
import { datePickerConfig, datePickerPt } from './datePicker'
import { dialogConfig, dialogPt } from './dialog'
import { inputGroupConfig } from './inputGroup'
import { inputMaskPt } from './inputMask'
import { inputNumberConfig, inputNumberPt } from './inputNumber'
// import { textareaPt } from './textarea';

import { inputTextConfig, inputTextPt } from './inputText'
import { paginatorConfig, paginatorPt } from './paginator'
import { selectConfig, selectPt } from './select'
import { tableConfig, tablePt } from './table'
import { tagConfig, tagPt } from './tag'
import { textareaPt } from './textarea'
import { toggleButtonConfig, toggleButtonPt } from './toggleButton'
import { toggleSwitchConfig } from './toggleSwitch'
import { floatLabelPt } from './floatLabel'
import { tieredMenuPt } from './tieredMenu'
import { toastConfig } from './toast'
import { passwordPt } from './password'
import { panelMenuConfig, panelMenuPt } from './panelMenu'

const customPreset = definePreset(Aura, {
  primitive: {
    borderRadius: {
      none: '0',
      xs: '.3rem',
      sm: '.5rem',
      md: '10px',
      lg: '1rem'
    }
  },

  semantic: {
    primary: {
      50: 'var(--primary-50)',
      100: 'var(--primary-100)',
      200: 'var(--primary-200)',
      300: 'var(--primary-300)',
      400: 'var(--primary-400)',
      500: 'var(--primary-500)',
      600: 'var(--primary-600)',
      700: 'var(--primary-700)',
      800: 'var(--primary-800)',
      900: 'var(--primary-900)',
      950: 'var(--primary-950)'
    },

    green: {
      50: 'rgba(204, 244, 212, 1)',
      100: 'rgba(174, 224, 182, 1)',
      200: 'rgba(153, 226, 114, 1)',
      300: 'rgba(102, 211, 100, 1)',
      400: 'rgba(51, 196, 86, 1)',
      500: 'rgba(0, 181, 72, 1)',
      600: 'rgba(0, 161, 65, 1)',
      700: 'rgba(0, 141, 57, 1)',
      800: 'rgba(0, 121, 50, 1)',
      900: 'rgba(0, 101, 43, 1)',
      950: 'rgba(0, 81, 35, 1)'
    },

    red: {
      50: 'rgb(255, 230, 225)',
      100: 'rgb(255, 204, 198)',
      200: 'rgb(255, 153, 150)',
      300: 'rgb(255, 102, 102)',
      400: 'rgb(255, 51, 55)',
      500: 'rgb(197, 24, 0)',
      600: 'rgb(179, 22, 0)',
      700: 'rgb(161, 19, 0)',
      800: 'rgb(143, 16, 0)',
      900: 'rgb(102, 11, 0)',
      950: 'rgb(77, 8, 0)'
    },

    iconSize: '1.5rem',

    colorScheme: {
      light: {
        primary: {
          color: '{primary.500}'
        },
        formField: {
          background: 'var(--form-element-background)',
          borderColor: 'var(--primary-700)',
          hoverBorderColor: 'var(--primary-800)',
          focusBorderColor: 'var(--primary-900)'
        }
      },
      dark: {
        primary: {
          color: '{primary.500}'
        },
        formField: {
          background: 'var(--form-element-background)',
          borderColor: 'var(--primary-400)',
          hoverBorderColor: 'var(--primary-300)',
          focusBorderColor: 'var(--primary-300)'
        }
      }
    },

    formField: {
      paddingX: '1rem',
      paddingY: '1rem'
    }
  },

  components: {
    button: {
      colorScheme: {
        light: buttonsConfig(),
        dark: buttonsConfig()
      }
    },

    togglebutton: toggleButtonConfig(),

    card: cardConfig(),

    dialog: dialogConfig(),

    inputtext: inputTextConfig(),

    inputnumber: inputNumberConfig(),

    inputgroup: inputGroupConfig(),

    datatable: tableConfig(),

    paginator: paginatorConfig(),

    checkbox: checkBoxConfig(),

    select: selectConfig(),

    datepicker: datePickerConfig(),

    toggleswitch: toggleSwitchConfig(),

    tag: tagConfig(),

    toast: toastConfig(),

    panelmenu: panelMenuConfig(),

    drawer: {
      light: {
        background: 'var(--site-bg)'
      },
      dark: {
        background: 'var(--site-bg)'
      }
    }
  }
})

export const options: PrimeVueConfiguration = {
  ripple: true,

  theme: {
    preset: customPreset,
    options: {
      darkModeSelector: '.app-dark'
    }
  },

  locale: {
    monthNames: [
      'Январь',
      'Февраль',
      'Март',
      'Апрель',
      'Май',
      'Июнь',
      'Июль',
      'Август',
      'Сентрябрь',
      'Октябрь',
      'Ноябрь',
      'Декабрь'
    ],
    dayNames: ['Воскресенье', 'Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота'],
    dayNamesMin: ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'],
    firstDayOfWeek: 1,
    noFileChosenMessage: 'Нет изображения',
    emptyMessage: 'Нет данных',
    emptyFilterMessage: 'Ничего не найдено'
  } as PrimeVueLocaleOptions,

  pt: {
    inputnumber: inputNumberPt(),

    textarea: textareaPt(),

    inputtext: inputTextPt(),

    inputmask: inputMaskPt(),

    dialog: dialogPt(),

    card: cardPt(),

    tag: tagPt(),

    select: selectPt(),

    togglebutton: toggleButtonPt(),

    paginator: paginatorPt(),

    datatable: tablePt(),

    floatlabel: floatLabelPt(),

    tieredmenu: tieredMenuPt(),

    password(instance) {
      return passwordPt(instance)
    },

    datepicker(instance) {
      return datePickerPt(instance)
    },

    panelmenu: panelMenuPt(),
  }
}
